import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { motion } from "framer-motion";
import RotatePhonePopup from '../RotatePhonePopup';
import { useSnackbar } from "../context/SnackbarContext";
import { useTranslatedText } from '../../hooks/useTranslatedText';

import audio from "../../audio/yeah.mp3"

import star from "../../img/star.svg"
import { convertToNativeNumerals } from '../../utils/numberUtils';
import { useLanguage } from '../context/Languagecontext';
import LanguageDropdown from '../Languagedropdown';

// Custom High-Quality Scratch Card Component

const HighQualityScratchCard = ({
    width,
    height,
    image,
    finishPercent = 50,
    brushSize = 30,
    onComplete,
    children,
    currentQuestion
}) => {
    const canvasRef = useRef(null);
    const [isScratching, setIsScratching] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [canvasOpacity, setCanvasOpacity] = useState(1);
    const [dimensions, setDimensions] = useState({ width, height });
    const [imageLoaded, setImageLoaded] = useState(false);

    const isInitialized = useRef(false);

    useEffect(() => {
        const handleResize = () => {
            setDimensions({ width, height });
            isInitialized.current = false;
            setImageLoaded(false);
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, [width, height]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || isInitialized.current) return;

        const ctx = canvas.getContext('2d', {
            willReadFrequently: true,
            alpha: true
        });

        const img = new Image();
        img.crossOrigin = "anonymous";

        img.onload = () => {
            const dpr = window.devicePixelRatio || 1;
            canvas.width = dimensions.width * dpr;
            canvas.height = dimensions.height * dpr;
            canvas.style.width = `${dimensions.width}px`;
            canvas.style.height = `${dimensions.height}px`;

            ctx.scale(dpr, dpr);
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, dimensions.width, dimensions.height);

            isInitialized.current = true;
            setImageLoaded(true);
        };

        img.src = image;

        return () => {
            isInitialized.current = false;
            setImageLoaded(false);
        };
    }, [image, dimensions.width, dimensions.height]);

    const getPosition = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        if (e.touches && e.touches[0]) {
            return {
                x: (e.touches[0].clientX - rect.left) * scaleX,
                y: (e.touches[0].clientY - rect.top) * scaleY
            };
        }
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY
        };
    };

    const scratch = (x, y) => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;

        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.arc(x / dpr, y / dpr, brushSize, 0, 2 * Math.PI);
        ctx.fill();

        if (Math.random() < 0.1) {
            checkCompletion();
        }
    };

    const checkCompletion = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        let transparent = 0;
        for (let i = 3; i < pixels.length; i += 4) {
            if (pixels[i] < 128) transparent++;
        }

        const percentScratched = (transparent / (pixels.length / 4)) * 100;

        if (percentScratched > finishPercent && !isComplete) {
            setIsComplete(true);

            const startTime = performance.now();
            const duration = 300;

            const fadeOut = (currentTime) => {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);

                const easeOut = 1 - Math.pow(1 - progress, 3);
                setCanvasOpacity(1 - easeOut);

                if (progress < 1) {
                    requestAnimationFrame(fadeOut);
                } else {
                    if (onComplete) {
                        onComplete();
                    }
                }
            };

            requestAnimationFrame(fadeOut);
        }
    };

    const handleStart = (e) => {
        e.preventDefault();
        setIsScratching(true);
        const pos = getPosition(e);
        scratch(pos.x, pos.y);
    };

    const handleMove = (e) => {
        if (!isScratching) return;
        e.preventDefault();
        const pos = getPosition(e);
        scratch(pos.x, pos.y);
    };

    const handleEnd = (e) => {
        e.preventDefault();
        setIsScratching(false);
    };

    return (
        <div style={{
            position: 'relative',
            width: `${dimensions.width}px`,
            height: `${dimensions.height}px`,
            userSelect: 'none',
            WebkitUserSelect: 'none',
            touchAction: 'none'
        }}>
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 1,
                opacity: imageLoaded ? 1 : 0,
                transition: 'opacity 0.2s ease'
            }}>
                {children}
            </div>

            {!imageLoaded && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 3,
                    background: currentQuestion?.bg || 'rgba(0, 0, 0, 0.8)',
                    borderRadius: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div className="custom-spinner" style={{ borderTopColor: currentQuestion?.color || '#fff' }} />
                </div>
            )}

            <canvas
                ref={canvasRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    cursor: onComplete ? 'pointer' : 'default',
                    zIndex: 2,
                    touchAction: 'none',
                    pointerEvents: isComplete ? 'none' : (onComplete && imageLoaded ? 'auto' : 'none'),
                    opacity: imageLoaded ? canvasOpacity : 0,
                    transition: 'opacity 0.2s ease'
                }}
                onMouseDown={onComplete && imageLoaded ? handleStart : undefined}
                onMouseMove={onComplete && imageLoaded ? handleMove : undefined}
                onMouseUp={onComplete && imageLoaded ? handleEnd : undefined}
                onMouseLeave={onComplete && imageLoaded ? handleEnd : undefined}
                onTouchStart={onComplete && imageLoaded ? handleStart : undefined}
                onTouchMove={onComplete && imageLoaded ? handleMove : undefined}
                onTouchEnd={onComplete && imageLoaded ? handleEnd : undefined}
            />
        </div>
    );
};

const questions = [
    {
        image: "https://res.cloudinary.com/dbq3h7ucj/image/upload/v1768042939/s1h_j55b2b.png",
        color: "#41C9C9",
        border: "3px solid #41C9C9",
        border2: "1px solid #41C9C9",
        background: "rgba(65, 201, 201, 0.18)",
        bg: "linear-gradient(180deg, rgba(65,201,201,0.15) 0%, rgba(65,201,201,0.20) 100%)",
        boxShadow: "0 0 20px 1px #41C9C9",
        glass: `
            0 8px 32px rgba(0,0,0,0.37),
            0 0 20px 1px rgba(65,201,201,0.6),
            inset 0 15px 20px rgba(255,255,255,0.7),
            inset 0 -2px 10px rgba(0,0,0,0.2),
            inset 0 0 80px 20px rgba(65,201,201,0.3),
            inset 0 0 120px 20px rgba(65,201,201,0.15)
        `
    },
    {
        image: "https://res.cloudinary.com/dbq3h7ucj/image/upload/v1768042939/s2h_k2lkzi.png",
        color: "#7C31EC",
        border: "3px solid #7C31EC",
        border2: "1px solid #7C31EC",
        background: "rgba(124, 49, 236, 0.18)",
        bg: "linear-gradient(180deg, rgba(124, 49, 236,0.15) 0%, rgba(124, 49, 236,0.20) 100%)",
        boxShadow: "0 0 20px 1px #7C31EC",
        glass: `
            0 8px 32px rgba(0,0,0,0.37),
            0 0 20px 1px rgba(124, 49, 236,0.6),
            inset 0 15px 20px rgba(255,255,255,0.7),
            inset 0 -2px 10px rgba(0,0,0,0.2),
            inset 0 0 80px 20px rgba(124, 49, 236,0.3),
            inset 0 0 120px 20px rgba(124, 49, 236,0.15)
        `
    },
    {
        image: "https://res.cloudinary.com/dbq3h7ucj/image/upload/v1768042939/s3h_mirfke.png",
        color: "#00A27D",
        border: "3px solid #00A27D",
        border2: "1px solid #00A27D",
        background: "rgba(0, 162, 125, 0.18)",
        bg: "linear-gradient(180deg, rgba(0, 162, 125,0.15) 0%, rgba(0, 162, 125,0.20) 100%)",
        boxShadow: "0 0 20px 1px #00A27D",
        glass: `
            0 8px 32px rgba(0,0,0,0.37),
            0 0 20px 1px rgba(0, 162, 125,0.6),
            inset 0 15px 20px rgba(255,255,255,0.7),
            inset 0 -2px 10px rgba(0,0,0,0.2),
            inset 0 0 80px 20px rgba(0, 162, 125,0.3),
            inset 0 0 120px 20px rgba(0, 162, 125,0.15)
        `
    },
    {
        image: "https://res.cloudinary.com/dbq3h7ucj/image/upload/v1768042939/s4h_gbpmhd.png",
        color: "#FE9D63",
        border: "3px solid #FE9D63",
        border2: "1px solid #FE9D63",
        background: "rgba(254, 157, 99, 0.18)",
        bg: "linear-gradient(180deg, rgba(254, 157, 99,0.15) 0%, rgba(254, 157, 99,0.20) 100%)",
        boxShadow: "0 0 20px 1px #FE9D63",
        glass: `
            0 8px 32px rgba(0,0,0,0.37),
            0 0 20px 1px rgba(254, 157, 99,0.6),
            inset 0 15px 20px rgba(255,255,255,0.7),
            inset 0 -2px 10px rgba(0,0,0,0.2),
            inset 0 0 80px 20px rgba(254, 157, 99,0.3),
            inset 0 0 120px 20px rgba(254, 157, 99,0.15)
        `
    },
    {
        image: "https://res.cloudinary.com/dbq3h7ucj/image/upload/v1768042938/s5h_qox5o1.png",
        color: "#FF346A",
        border: "3px solid #FF346A",
        border2: "1px solid #FF346A",
        background: "rgba(255, 52, 106, 0.18)",
        bg: "linear-gradient(180deg, rgba(255, 52, 106,0.15) 0%, rgba(255, 52, 106,0.20) 100%)",
        boxShadow: "0 0 20px 1px #FF346A",
        glass: `
            0 8px 32px rgba(0,0,0,0.37),
            0 0 20px 1px rgba(255, 52, 106,0.6),
            inset 0 15px 20px rgba(255,255,255,0.7),
            inset 0 -2px 10px rgba(0,0,0,0.2),
            inset 0 0 80px 20px rgba(255, 52, 106,0.3),
            inset 0 0 120px 20px rgba(255, 52, 106,0.15)
        `
    }
];

const StepPage = () => {
    const { lanText, t } = useLanguage();
    const location = useLocation();
    const { username, category } = useParams();
    const { hint, uploadedImage, originalData, avatar } = location.state || {};

    // ✅ Derive raw titles BEFORE hooks (safe because no hook called yet)
    const rawTitles = originalData?.annoycardvalue
        ? originalData.annoycardvalue
        : t.defaultcardtitle;
    const rawOnlyFour = (rawTitles || []).slice(0, 4);

    // ✅ All useTranslatedText hooks at top level, before any early return
    const translatedNickname = useTranslatedText("Nickname", lanText);
    const translatedT0 = useTranslatedText(rawOnlyFour[0] || "", lanText);
    const translatedT1 = useTranslatedText(rawOnlyFour[1] || "", lanText);
    const translatedT2 = useTranslatedText(rawOnlyFour[2] || "", lanText);
    const translatedT3 = useTranslatedText(rawOnlyFour[3] || "", lanText);

    // ✅ CardTitle always reflects current language
    const CardTitle = [
        translatedNickname,
        translatedT0,
        translatedT1,
        translatedT2,
        translatedT3,
    ];

    const [show, setShow] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answer, setAnswer] = useState("");


    // ✅ annoyans stored as { index: answerString } — language-change safe
    const [annoyans, setAnnoyans] = useState(() => {
        const saved = sessionStorage.getItem('steppage_annoyans');
        return saved ? JSON.parse(saved) : {};
    });

    const navigate = useNavigate();
    const [isShaking, setIsShaking] = useState(false);
    const mainContainerRef = useRef(null);
    const textareaRef = useRef(null);
    const audioRef = useRef(null);
    const topRef = useRef(null);
    const cardContainerRef = useRef(null);
    const scrollTimeoutRef = useRef(null);
    const [scratchedCards, setScratchedCards] = useState(() => {
        const saved = sessionStorage.getItem('steppage_scratched');
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });
    const [isCheckingConnection, setIsCheckingConnection] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [focus, setFocus] = useState(false);
    const { showSnackbar } = useSnackbar();
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const [orientation, setOrientation] = useState(window.innerWidth > window.innerHeight ? 'landscape' : 'portrait');

    const isSmallScreen = window.innerWidth < 380 && window.innerWidth !== 360;
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const width = window.innerWidth;
    const height = window.innerHeight;

    const isInvalidData = originalData === "undefined" || !originalData;
    const [isEditingModal, setIsEditingModal] = useState(false);

    const CHARACTER_LIMITS = {
        0: 10,
        1: 15,
        2: 15,
        3: 15,
        4: 15
    };

    const isInAppBrowser = () => {
        const ua = navigator.userAgent || navigator.vendor || window.opera;
        return /Instagram|FBAN|FBAV|Messenger|Telegram/i.test(ua);
    };

    // ✅ Get answer by index (language-safe)
    const getAnswerForIndex = (index) => {
        return annoyans[index] || "";
    };

    // ✅ Check all 5 answers filled by index
    const areAllAnswersFilled = () => {
        return CardTitle.every((_, index) =>
            annoyans[index] && annoyans[index].trim() !== ''
        );
    };

    const handleCloseModal = () => {
        if (answer.trim()) {
            // ✅ Store by index
            const newAnnoyans = { ...annoyans, [currentIndex]: answer.trim() };
            setAnnoyans(newAnnoyans);
            sessionStorage.setItem('steppage_annoyans', JSON.stringify(newAnnoyans));
            sessionStorage.setItem('steppage_scratched', JSON.stringify([...scratchedCards]));
        } else {
            // ✅ Remove by index
            const { [currentIndex]: _, ...newAnnoyans } = annoyans;
            setAnnoyans(newAnnoyans);
            sessionStorage.setItem('steppage_annoyans', JSON.stringify(newAnnoyans));
            sessionStorage.setItem('steppage_scratched', JSON.stringify([...scratchedCards]));
        }

        setIsClosing(true);
        setTimeout(() => {
            if (window.history.state?.fullscreen) {
                window.history.back();
            }
            setIsClosing(false);
        }, 300);
    };

    const handleBlur = () => {
        setFocus(false);

        if (answer.trim()) {
            // ✅ Store by index
            const newAnnoyans = { ...annoyans, [currentIndex]: answer.trim() };
            setAnnoyans(newAnnoyans);
            sessionStorage.setItem('steppage_annoyans', JSON.stringify(newAnnoyans));
            sessionStorage.setItem('steppage_scratched', JSON.stringify([...scratchedCards]));
        } else {
            // ✅ Remove by index
            const { [currentIndex]: _, ...newAnnoyans } = annoyans;
            setAnnoyans(newAnnoyans);
            sessionStorage.setItem('steppage_annoyans', JSON.stringify(newAnnoyans));
            sessionStorage.setItem('steppage_scratched', JSON.stringify([...scratchedCards]));
        }

        setTimeout(() => {
            handleCloseModal();
        }, 300);
    };

    const handleBlurRef = useRef(handleBlur);
    useEffect(() => {
        handleBlurRef.current = handleBlur;
    }, [handleBlur]);

    useEffect(() => {
        if (!show) return;
        const viewport = window.visualViewport;
        if (!viewport) return;
        let lastHeight = viewport.height;

        const handleResize = () => {
            if (viewport.height > lastHeight + 100) {
                if (focus) {
                    setFocus(false);
                    handleBlurRef.current();
                }
            }
            lastHeight = viewport.height;
        };

        viewport.addEventListener('resize', handleResize);
        return () => viewport.removeEventListener('resize', handleResize);
    }, [show, focus]);

    // Preload all scratch card images
    useEffect(() => {
        const imageUrls = questions.map(q => q.image);

        const imagePromises = imageUrls.map((url) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve();
                img.onerror = reject;
                img.src = url;
            });
        });

        Promise.all(imagePromises)
            .then(() => setImagesLoaded(true))
            .catch((error) => {
                console.error('Error loading images:', error);
                setImagesLoaded(true);
            });
    }, []);

    // Handle orientation changes
    useEffect(() => {
        const handleOrientationChange = () => {
            const newOrientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
            setOrientation(newOrientation);

            if (cardContainerRef.current) {
                cardContainerRef.current.style.display = 'none';
                requestAnimationFrame(() => {
                    if (cardContainerRef.current) {
                        cardContainerRef.current.style.display = 'flex';
                    }
                });
            }
        };

        window.addEventListener('resize', handleOrientationChange);
        window.addEventListener('orientationchange', handleOrientationChange);

        return () => {
            window.removeEventListener('resize', handleOrientationChange);
            window.removeEventListener('orientationchange', handleOrientationChange);
        };
    }, []);

    useEffect(() => {
        if (isInvalidData) {
            navigate(`/${username}/${category}`, { replace: true });
        }
    }, [isInvalidData, category, username, navigate]);

    useEffect(() => {
        const handlePopState = (event) => {
            if (!event.state?.fullscreen) {
                setShow(false);
                setIsEditingModal(false);
            }
        };

        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, []);

    useEffect(() => {
        const preventScroll = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };

        const preventTouchMove = (e) => {
            const scratchCardElement = cardContainerRef.current;
            if (scratchCardElement && scratchCardElement.contains(e.target)) return;
            if (e.target.tagName === 'CANVAS') return;
            e.preventDefault();
            e.stopPropagation();
        };

        let scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overscrollBehavior = 'none';
        document.documentElement.style.overscrollBehavior = 'none';

        document.addEventListener('touchmove', preventTouchMove, { passive: false, capture: true });
        document.addEventListener('wheel', preventScroll, { passive: false });
        document.addEventListener('scroll', preventScroll, { passive: false });

        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            document.body.style.overscrollBehavior = '';
            document.documentElement.style.overflow = '';
            document.documentElement.style.overscrollBehavior = '';
            document.removeEventListener('touchmove', preventTouchMove, { capture: true });
            document.removeEventListener('wheel', preventScroll);
            document.removeEventListener('scroll', preventScroll);
        };
    }, []);

    // ✅ Load existing answer by index when modal opens
    useEffect(() => {
        if (show) {
            const existingAnswer = annoyans[currentIndex] || "";
            setAnswer(existingAnswer);
        }
        else {
            setAnswer("");
        }
    }, [show, currentIndex]);


    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const isDisabled = isCheckingConnection || show;
        window.dispatchEvent(new CustomEvent('dropdownDisabledState', {
            detail: { disabled: isDisabled }
        }));
    }, [isCheckingConnection, show]);

    // ✅ Early return AFTER all hooks
    if (isInvalidData) {
        return null;
    }

    const handleFocus = (e) => {
        setFocus(true);
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;

        scrollTimeoutRef.current = setTimeout(() => {
            requestAnimationFrame(() => {
                if (topRef.current) {
                    const element = topRef.current;
                    const rect = element.getBoundingClientRect();
                    const scrollTop = window.scrollY || document.documentElement.scrollTop;
                    const targetY = rect.top + scrollTop;
                    window.scrollTo({ top: targetY, behavior: "smooth" });
                }
            });
        }, 100);

        setIsEditingModal(true);
    };

    const checkInternetConnection = async () => {
        await new Promise(resolve => setTimeout(resolve, 200));

        if (!navigator.onLine) return false;

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            clearTimeout(timeoutId);
            return true;
        } catch (error) {
            console.log('Network check failed:', error);
            return false;
        }
    };

    const handleNextButton = async () => {
        setIsCheckingConnection(true);
        const isOnline = await checkInternetConnection();
        setIsCheckingConnection(false);

        if (!isOnline) {
            showSnackbar(t?.common?.nointertoast || "Please turn on internet connection", "error");
            return;
        }

        const sanitizeText = (text) => {
            try { return text.normalize('NFC').trim(); }
            catch (e) { return text.trim(); }
        };

        // ✅ KEY FIX: only flush current `answer` if modal is open
        // If modal is closed, `answer` is stale/empty — don't overwrite saved annoyans
        const flushedAnnoyans = show
            ? { ...annoyans, [currentIndex]: answer }
            : { ...annoyans };

        const sanitizedAnnoyans = Object.fromEntries(
            Object.entries(flushedAnnoyans).map(([idx, ans]) => [idx, sanitizeText(ans)])
        );

        setAnnoyans(sanitizedAnnoyans);

        const nicknameAnswer = sanitizedAnnoyans[0] || "";

        const annoyansWithoutNickname = CardTitle.slice(1).map((title, i) => ({
            question: title,
            answer: sanitizedAnnoyans[i + 1] || ""
        }));

        setShow(false);
        window.scrollTo(0, 0);

        sessionStorage.setItem('steppage_annoyans', JSON.stringify(sanitizedAnnoyans));
        sessionStorage.setItem('steppage_scratched', JSON.stringify([...scratchedCards]));

        const formData = {
            originalData,
            hint,
            uploadedImage,
            avatar,
            nickname: nicknameAnswer,
            annoyans: annoyansWithoutNickname
        };

        navigate(`/${username || ''}/${category || ''}/funcards/card`, { state: formData });
    };

    const triggerShake = () => {
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 600);
    };

    const currentQuestion = questions[currentIndex];

    const handleScratchComplete = (index) => {
        console.log(`Card ${index + 1} complete`);

        if (audioRef.current) {
            audioRef.current.play().catch(err => {
                console.log('Audio play failed:', err);
            });
        }

        setTimeout(() => {
            setIsEditingModal(false);
            setScratchedCards(prev => new Set([...prev, index]));
        }, 0);
    };

    return (
        <div className="bg-black GTRegular scratch" ref={mainContainerRef} 
            style={{height : "100dvh"}}>
            <audio ref={audioRef} src={audio} preload="auto" />
            <div className="h-100 pt-42">
                <div
                    className="mx-auto"
                    style={{
                        width: "450px",
                        maxWidth: "100vw",
                        height: isIOS ? '99dvh' : '99dvh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                >
                    {/* FIXED TOP SECTION */}
                    <div
                        className="d-flex flex-column align-items-center justify-content-center gap-1 px-2 pt-2 position-relative"
                        style={{ height: '90px', flexShrink: 0 }}
                    >
                        <p
                            className="GTMedium text-white m-0 text-center"
                            style={{
                                lineHeight: "20px",
                                fontSize: t.annoyCard.scratch?.length > 60
                                    ? "clamp(14px, 2vw, 18px)"
                                    : t.annoyCard.scratch?.length > 40
                                        ? "clamp(18px, 2.5vw, 24px)"
                                        : "clamp(22px, 3vw, 32px)",
                                zIndex: 5
                            }}
                        >
                            {t.annoyCard.scratch}
                        </p>

                        <p
                            className="text-center m-0"
                            style={{
                                color: "#fff",
                                fontSize: t.annoyCard.subTitle?.length > 60
                                    ? "clamp(10px, 1.8vw, 13px)"
                                    : t.annoyCard.subTitle?.length > 40
                                        ? "clamp(12px, 2vw, 15px)"
                                        : "clamp(14px, 2.2vw, 16px)",
                                zIndex: 5
                            }}
                        >
                            {t.annoyCard.subTitle}
                        </p>

                        <div
                            className="position-absolute"
                            style={{
                                bottom: "-48px",
                                left: "-20px",
                                height: "70px",
                                width: "120%",
                                pointerEvents: "none",
                                transform: "scaleY(-1)",
                                zIndex: 4
                            }}
                        >
                            <svg width="500" height="75" viewBox="0 0 500 75" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="500" height="75" fill="url(#paint0_linear_471_133)" />
                                <defs>
                                    <linearGradient id="paint0_linear_471_133" x1="250" y1="0" x2="250" y2="75" gradientUnits="userSpaceOnUse">
                                        <stop offset="0.182692" stopOpacity="0" />
                                        <stop offset="0.730769" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>
                    </div>

                    {/* SCROLLABLE MIDDLE SECTION */}
                    <div
                        ref={cardContainerRef}
                        key={orientation}
                        className="text-center d-flex flex-column align-items-center justify-content-start flex-grow-1 w-100"
                        style={{
                            overflow: 'auto',
                            overflowX: 'hidden',
                            WebkitOverflowScrolling: 'touch',
                            touchAction: 'pan-y',
                            position: 'relative'
                        }}
                    >
                        <div className="scratch-container px-2" style={{ paddingBottom: "50px", paddingTop: "20px" }}>
                            {!imagesLoaded ? (
                                questions.map((_, index) => (
                                    <div
                                        key={`shimmer-${index}`}
                                        className="position-relative scratch-box"
                                        style={{ width: "100%", height: "100%", flexShrink: 0 }}
                                    >
                                        <div className='pt-3 mt-1' style={{ width: "100%", height: "100%" }}>
                                            <div
                                                className="shimmer2"
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    borderRadius: "1rem",
                                                    backgroundSize: "200% 100%",
                                                    animation: "shimmer 1.5s infinite"
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))
                            ) : (
                                questions
                                    .map((question, index) => ({ question, index }))
                                    .sort((a, b) => {
                                        const aScratched = scratchedCards.has(a.index);
                                        const bScratched = scratchedCards.has(b.index);
                                        const aAnswer = getAnswerForIndex(a.index);
                                        const bAnswer = getAnswerForIndex(b.index);
                                        const aHasAnswer = aAnswer && aAnswer.trim() !== "";
                                        const bHasAnswer = bAnswer && bAnswer.trim() !== "";

                                        const aPriority = !aScratched ? 0 : (aHasAnswer ? 2 : 1);
                                        const bPriority = !bScratched ? 0 : (bHasAnswer ? 2 : 1);

                                        return aPriority - bPriority;
                                    })
                                    .map(({ question, index }) => {
                                        const isScratched = scratchedCards.has(index);
                                        const cardAnswer = getAnswerForIndex(index);
                                        const hasAnswer = cardAnswer && cardAnswer.trim() !== "";

                                        return (
                                            <div
                                                key={`${orientation}-${index}`}
                                                onClick={() => {
                                                    if (isScratched) {
                                                        setCurrentIndex(index);
                                                        setAnswer(cardAnswer || "");
                                                        const url = new URL(window.location.href);
                                                        url.searchParams.set('fullscreen', '1');
                                                        window.history.pushState({ fullscreen: true }, '', url.toString());
                                                        setShow(true);
                                                    }
                                                }}
                                                className="position-relative scratch-box"
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    cursor: isScratched ? "pointer" : "default",
                                                    flexShrink: 0,
                                                    zIndex: hasAnswer ? 3 : isScratched ? 2 : 1,
                                                }}
                                            >
                                                {/* Layer 1: Card background */}
                                                <div style={{ width: "100%", height: "100%" }}>
                                                    <div
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            textAlign: "center",
                                                            borderRadius: "1rem",
                                                            background: question.bg,
                                                            backdropFilter: "blur(20px)",
                                                            WebkitBackdropFilter: "blur(20px)",
                                                            boxShadow: question.glass,
                                                            color: "white",
                                                        }}
                                                    >
                                                        <div
                                                            className="position-relative px-1"
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                alignItems: "center",
                                                                backgroundSize: "cover",
                                                                backgroundPosition: "center",
                                                                backgroundRepeat: "no-repeat",
                                                                textAlign: "center",
                                                                borderRadius: "1rem"
                                                            }}
                                                        >
                                                            <div
                                                                className="w-100 p-2 h-100 d-flex flex-column align-items-center justify-content-between"
                                                                style={{ marginTop: "10px" }}
                                                            >
                                                                <p
                                                                    className="GTRegular h-50 d-flex align-items-center justify-content-center"
                                                                    style={{
                                                                        fontSize: "14px",
                                                                        fontWeight: "500",
                                                                        lineHeight: "14px",
                                                                        wordBreak: "break-word"
                                                                    }}
                                                                >
                                                                    {t.annoyCard.their} {CardTitle[index].toLowerCase()}?
                                                                </p>

                                                                <div
                                                                    onClick={(e) => {
                                                                        if (isScratched) {
                                                                            e.stopPropagation();
                                                                            setCurrentIndex(index);
                                                                            setAnswer(cardAnswer || "");
                                                                            const url = new URL(window.location.href);
                                                                            url.searchParams.set('fullscreen', '1');
                                                                            window.history.pushState({ fullscreen: true }, '', url.toString());
                                                                            setShow(true);
                                                                        }
                                                                    }}
                                                                    className={`GTRegular p-2 ${isShaking && currentIndex === index ? 'shake-animation' : ''}`}
                                                                    style={{
                                                                        width: "100%",
                                                                        height: "55%",
                                                                        fontSize: hasAnswer ? "13px" : "11px",
                                                                        fontWeight: "500",
                                                                        lineHeight: "14px",
                                                                        borderRadius: "12px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        position: "relative",
                                                                        cursor: isScratched ? "pointer" : "default",
                                                                        textAlign: "center",
                                                                        background: `linear-gradient(90deg, rgba(32, 32, 32,0.6) 0%, rgba(56, 56, 56,0.6) 100%)`,
                                                                        backdropFilter: "blur(25px)",
                                                                        WebkitBackdropFilter: "blur(25px)",
                                                                        border: "1px solid rgba(255,255,255,0.25)",
                                                                        wordBreak: "break-word",
                                                                        color: hasAnswer ? "white" : "#BEBEBE"
                                                                    }}
                                                                >
                                                                    {hasAnswer ? cardAnswer : `${t.annoyCard.them} ${CardTitle[index].toLowerCase()}`}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Layer 2: Scratch overlay (only if not scratched) */}
                                                {!isScratched && (
                                                    <div
                                                        onClick={() => {
                                                            setCurrentIndex(index);
                                                            setAnswer(cardAnswer || "");
                                                            const url = new URL(window.location.href);
                                                            url.searchParams.set('fullscreen', '1');
                                                            window.history.pushState({ fullscreen: true }, '', url.toString());
                                                            setShow(true);
                                                        }}
                                                        className="d-flex flex-column align-items-center justify-content-center h-100 w-100"
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            cursor: "pointer",
                                                            zIndex: 10
                                                        }}
                                                    >
                                                        <img src={question.image} className='w-100 h-100 img-fluid' alt={`scratch-${index}`} />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                            )}
                        </div>
                    </div>

                    {/* FIXED BOTTOM SECTION */}
                    <div
                        className='mx-auto mb-4 position-relative'
                        style={{ width: "90%", flexShrink: 0 }}
                    >
                        <Button
                            onClick={handleNextButton}
                            disabled={!areAllAnswersFilled() || isCheckingConnection}
                            className="rounded-pill mx-auto GTMedium text-white border-0"
                            style={{
                                width: "100%",
                                padding: "14px 1px",
                                height: "54px",
                                background: "#E52F5F",
                                transform: "scale(0.95)",
                                opacity: !areAllAnswersFilled() ? 0.6 : 1,
                                transition: 'all 0.3s ease',
                                zIndex: 5,
                                position: "relative",
                                cursor: !areAllAnswersFilled() || isCheckingConnection ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {isCheckingConnection ? (
                                <div className="custom-spinner" style={{ borderTopColor: "#fff" }} />
                            ) : (
                                <span
                                    className='GTMedium'
                                    style={{
                                        fontSize: "clamp(21px, 4.5vw, 27px)",
                                        lineHeight: "1",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {t?.common?.next || "Next"}
                                </span>
                            )}
                        </Button>

                        <div
                            className="position-absolute"
                            style={{
                                bottom: "40px",
                                left: "-20px",
                                height: "70px",
                                width: "120%",
                                pointerEvents: "none",
                                zIndex: 3
                            }}
                        >
                            <svg width="500" height="75" viewBox="0 0 500 75" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="500" height="75" fill="url(#paint0_linear_471_133)" />
                                <defs>
                                    <linearGradient id="paint0_linear_471_133" x1="250" y1="0" x2="250" y2="75" gradientUnits="userSpaceOnUse">
                                        <stop offset="0.182692" stopOpacity="0" />
                                        <stop offset="0.730769" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>
                    </div>
                </div>

                {show && (
                    <div
                        className={`position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center modal-wrapper
                            ${focus && !(width === 360 && height === 696)
                                ? isIOS ? "align-items-end" : "align-items-center"
                                : "align-items-center"}`}
                        style={{
                            zIndex: 99999,
                            backgroundColor: 'rgba(0, 0, 0, 0.95)',
                            backdropFilter: isClosing ? 'blur(0px)' : 'blur(5px)'
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: (width === 360 && height === 696) ? 1 : 1.1 }}
                            exit={{ scale: 0 }}
                            transition={{ duration: 0.2, delay: 0.1 }}
                            className={`modal-scale ${isClosing ? "scaleOut" : "scaleIn"}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                width: "270px",
                                height: "270px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                marginBottom:
                                    focus
                                        ? isIOS
                                            ? "70px"
                                            : !(width === 360 && height === 696) ? "250px" : "-90px"
                                        : "0px",
                                transition: focus
                                    ? width === 360 && height === 696
                                        ? "margin-bottom 0.3s ease 0.3s"
                                        : "margin-bottom 0s ease"
                                    : "margin-bottom 0s ease",
                            }}
                        >
                            {scratchedCards.has(currentIndex) ? (
                                <div
                                    style={{
                                        width: "270px",
                                        height: "270px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center",
                                        borderRadius: "1rem",
                                        background: currentQuestion.bg,
                                        backdropFilter: "blur(20px)",
                                        WebkitBackdropFilter: "blur(20px)",
                                        boxShadow: currentQuestion.glass,
                                        color: "white",
                                    }}
                                >
                                    <div
                                        className="position-relative"
                                        style={{
                                            width: "270px",
                                            height: "270px",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                            backgroundRepeat: "no-repeat",
                                            textAlign: "center",
                                            borderRadius: "1rem"
                                        }}
                                    >
                                        <button
                                            onClick={handleCloseModal}
                                            style={{
                                                position: "absolute",
                                                top: "-120px",
                                                left: "50%",
                                                transform: "translateX(-50%)",
                                                background: "#B8B8B8",
                                                border: "none",
                                                borderRadius: "50%",
                                                width: "35px",
                                                height: "35px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                cursor: "pointer",
                                                zIndex: 10,
                                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faTimes} style={{ color: "#333", fontSize: "18px" }} />
                                        </button>

                                        <div
                                            style={{
                                                position: "absolute",
                                                width: "50%",
                                                height: "100%",
                                                top: "-20px",
                                                left: "10%",
                                                transform: "translateX(-50%)",
                                            }}
                                        >
                                            <img src={star} alt="star" />
                                        </div>

                                        <motion.div
                                            className="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-3 position-absolute overflow-hidden"
                                            style={{
                                                width: "65px",
                                                height: "65px",
                                                top: "-35px",
                                                left: "38%",
                                                transform: "translateX(-50%)",
                                            }}
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            exit={{ scale: 0 }}
                                            transition={{ duration: 0.2, ease: "easeOut" }}
                                        >
                                            <img
                                                src={originalData.avatar}
                                                alt="avatar"
                                                className="img-fluid rounded-circle"
                                            />
                                        </motion.div>

                                        <div
                                            className="w-100 p-3 h-100 d-flex flex-column align-items-center justify-content-between"
                                            style={{ marginTop: "40px" }}
                                        >
                                            <p
                                                className="GTRegular mb-3 h-50 d-flex align-items-center justify-content-center"
                                                style={{ fontSize: "18px", fontWeight: "500" }}
                                            >
                                                {t.annoyCard.their} {CardTitle[currentIndex].toLowerCase()}?
                                            </p>

                                            <div
                                                onClick={() => !isEditingModal && textareaRef.current?.focus()}
                                                className={`GTRegular h-50 ${isShaking ? 'shake-animation' : ''}`}
                                                style={{
                                                    width: "100%",
                                                    borderRadius: "12px",
                                                    padding: "12px 16px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    position: "relative",
                                                    cursor: isEditingModal ? "text" : "pointer",
                                                    textAlign: "center",
                                                    background: `linear-gradient(90deg, rgba(32, 32, 32,0.6) 0%, rgba(56, 56, 56,0.6) 100%)`,
                                                    backdropFilter: "blur(25px)",
                                                    WebkitBackdropFilter: "blur(25px)",
                                                    border: "1px solid rgba(255,255,255,0.25)",
                                                }}
                                            >
                                                <input
                                                    type="text"
                                                    className="custom-placeholder"
                                                    ref={textareaRef}
                                                    value={answer}
                                                    placeholder={`${t.annoyCard.them} ${CardTitle[currentIndex].toLowerCase()}`}
                                                    onChange={(e) => {
                                                        const text = e.target.value;
                                                        const limit = CHARACTER_LIMITS[currentIndex];
                                                        if ([...text].length > limit) {
                                                            triggerShake();
                                                        } else {
                                                            setAnswer(text);
                                                        }
                                                    }}
                                                    onFocus={handleFocus}
                                                    onBlur={handleBlur}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            textareaRef.current?.blur();
                                                        }
                                                    }}
                                                    enterKeyHint="done"
                                                    style={{
                                                        width: "100%",
                                                        outline: "none",
                                                        border: "none",
                                                        background: "transparent",
                                                        fontSize: "16px",
                                                        color: "#BEBEBE",
                                                        caretColor: currentQuestion.color,
                                                        textAlign: "center",
                                                        padding: "0 10px",
                                                    }}
                                                />

                                                <p
                                                    className="position-absolute m-0 GTMedium"
                                                    style={{ bottom: "1px", right: "8px", fontSize: "12px", color: "white" }}
                                                >
                                                    {convertToNativeNumerals([...answer].length, lanText)}/
                                                    {convertToNativeNumerals(CHARACTER_LIMITS[currentIndex], lanText)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ position: 'relative' }}>
                                    <button
                                        onClick={() => {
                                            setIsClosing(true);
                                            setTimeout(() => {
                                                if (window.history.state?.fullscreen) {
                                                    window.history.back();
                                                }
                                                setIsClosing(false);
                                            }, 300);
                                        }}
                                        style={{
                                            position: "absolute",
                                            top: "-110px",
                                            left: "50%",
                                            transform: "translateX(-50%)",
                                            background: "#B8B8B8",
                                            border: "none",
                                            borderRadius: "50%",
                                            width: "35px",
                                            height: "35px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            zIndex: 10,
                                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTimes} style={{ color: "#333", fontSize: "18px" }} />
                                    </button>

                                    <HighQualityScratchCard
                                        width={270}
                                        height={270}
                                        image={questions[currentIndex].image}
                                        finishPercent={20}
                                        brushSize={25}
                                        onComplete={() => handleScratchComplete(currentIndex)}
                                        currentQuestion={currentQuestion}
                                    >
                                        <div
                                            style={{
                                                width: "270px",
                                                height: "270px",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                textAlign: "center",
                                                borderRadius: "1rem",
                                                background: currentQuestion.bg,
                                                backdropFilter: "blur(20px)",
                                                WebkitBackdropFilter: "blur(20px)",
                                                boxShadow: currentQuestion.glass,
                                                color: "white",
                                            }}
                                        >
                                            <div
                                                className="position-relative"
                                                style={{
                                                    width: "270px",
                                                    height: "270px",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    backgroundSize: "cover",
                                                    backgroundPosition: "center",
                                                    backgroundRepeat: "no-repeat",
                                                    textAlign: "center",
                                                    borderRadius: "1rem"
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        width: "50%",
                                                        height: "100%",
                                                        top: "-20px",
                                                        left: "10%",
                                                        transform: "translateX(-50%)",
                                                    }}
                                                >
                                                    <img src={star} alt="star" />
                                                </div>

                                                <div
                                                    className="w-100 p-3 h-100 d-flex flex-column align-items-center justify-content-between"
                                                    style={{ marginTop: "40px" }}
                                                >
                                                    <p
                                                        className="GTRegular mb-3 h-50 d-flex align-items-center justify-content-center"
                                                        style={{ fontSize: "18px", fontWeight: "500" }}
                                                    >
                                                        {t.annoyCard.their} {CardTitle[currentIndex].toLowerCase()}?
                                                    </p>

                                                    <div
                                                        onClick={() => !isEditingModal && textareaRef.current?.focus()}
                                                        className={`h-50 GTRegular ${isShaking ? 'shake-animation' : ''}`}
                                                        style={{
                                                            width: "100%",
                                                            borderRadius: "12px",
                                                            padding: "12px 16px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            position: "relative",
                                                            cursor: isEditingModal ? "text" : "pointer",
                                                            textAlign: "center",
                                                            background: `linear-gradient(90deg, rgba(32, 32, 32,0.6) 0%, rgba(56, 56, 56,0.6) 100%)`,
                                                            backdropFilter: "blur(25px)",
                                                            WebkitBackdropFilter: "blur(25px)",
                                                            border: "1px solid rgba(255,255,255,0.25)",
                                                        }}
                                                    >
                                                        <input
                                                            type="text"
                                                            className="custom-placeholder"
                                                            ref={textareaRef}
                                                            value={answer}
                                                            placeholder={`${t.annoyCard.them} ${CardTitle[currentIndex].toLowerCase()}`}
                                                            onChange={(e) => {
                                                                const text = e.target.value;
                                                                const limit = CHARACTER_LIMITS[currentIndex];
                                                                if ([...text].length > limit) {
                                                                    triggerShake();
                                                                } else {
                                                                    setAnswer(text);
                                                                }
                                                            }}
                                                            onFocus={handleFocus}
                                                            onBlur={handleBlur}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    textareaRef.current?.blur();
                                                                }
                                                            }}
                                                            enterKeyHint="done"
                                                            style={{
                                                                width: "100%",
                                                                outline: "none",
                                                                border: "none",
                                                                background: "transparent",
                                                                fontSize: "16px",
                                                                color: "#BEBEBE",
                                                                caretColor: currentQuestion.color,
                                                                textAlign: "center",
                                                                padding: "0 10px",
                                                            }}
                                                        />

                                                        <p
                                                            className="position-absolute m-0 GTMedium"
                                                            style={{ bottom: "1px", right: "8px", fontSize: "12px", color: "white" }}
                                                        >
                                                            {convertToNativeNumerals([...answer].length, lanText)}/
                                                            {convertToNativeNumerals(CHARACTER_LIMITS[currentIndex], lanText)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </HighQualityScratchCard>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}
            </div>

            <RotatePhonePopup excludeWhen={[focus, show]} />
            <LanguageDropdown />
        </div>
    );
};

export default StepPage;